import React, { useEffect, useState } from "react";
import useScrollToTop from "../../hooks/useScrollToTop";
import {
  addAccreditation,
  getAccreditationsByCompetition,
  deleteAccreditation,
} from "./../../services/accreditationServices";
import { getUserCompetitions } from "./../../services/competitionsServices";
import { notifyAdmin } from "./../../utils/adminNotify";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";

const roles = [
  "Pesées",
  "Répartitions",
  ...Array.from({ length: 10 }, (_, i) => `Table ${i + 1}`),
];

const AccreditationsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userId = user?.userId; // ID de l'utilisateur connecté
  const userRole = getRoleFromRoleId(user?.role_id);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(""); // 👈 valeur vide par défaut
  const [accreditations, setAccreditations] = useState([]);

  useEffect(() => {
    if (userId) {
      loadCompetitions(userId);
    }
  }, [userId]);

  const loadCompetitions = async (id) => {
    const data = await getUserCompetitions(id);
    setCompetitions(data);

    if (data.length === 1) {
      setSelectedCompetition(data[0].id);
      loadAccreditations(data[0].id);
    }
  };

  const loadAccreditations = async (competitionId) => {
    const data = await getAccreditationsByCompetition(competitionId);
    setAccreditations(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompetition || !email || !role) return;

    const newAcc = {
      competition_id: selectedCompetition,
      user_email: email,
      role,
    };

    try {
      const res = await addAccreditation(newAcc);
      alert(`Invitation envoyée à ${email}`);

      // 🔔 Notification admin
      const message = `Nouvelle accréditation créée :
- Compétition ID : ${selectedCompetition}
- Email invité : ${email}
- Rôle : ${role}`;
      await notifyAdmin(message);

      setEmail("");
      setRole("Pesées");
      loadAccreditations(selectedCompetition);
    } catch (err) {
      console.error("Erreur lors de l'envoi de l'accréditation :", err);
      alert("Une erreur est survenue lors de l'envoi de l'accréditation.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette accréditation ?")) {
      await deleteAccreditation(id);
      loadAccreditations(selectedCompetition);
    }
  };

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
      <div className="text-center space-y-2 mb-8">
        <h2 className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400">
          ORGANISATEUR
        </h2>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          Accréditations
        </h1>
      </div>

      {/* Sélecteur de compétition */}
      <div className="mb-6 flex flex-col items-center">
        <label
          htmlFor="select-competition"
          className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Sélectionnez la compétition concernée :
        </label>
        <select
          id="select-competition"
          value={selectedCompetition}
          onChange={(e) => {
            setSelectedCompetition(e.target.value);
            loadAccreditations(e.target.value);
          }}
          className="p-2 border rounded-md dark:bg-gray-800 dark:text-white"
          required
        >
          <option value="">-- Sélectionner une compétition --</option>
          {competitions.map((comp) => (
            <option key={comp.id} value={comp.id}>
              {comp.nom_competition}
            </option>
          ))}
        </select>
      </div>

      {/* Formulaire d'ajout */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col items-center gap-4 mb-10"
      >
        <div className="flex flex-col sm:flex-row gap-4 items-center w-full justify-center">
          <div className="flex flex-col">
            <label htmlFor="email" className="text-sm font-medium mb-1">
              Email de la personne à accréditer :
            </label>
            <input
              id="email"
              type="email"
              placeholder="ex : nom@domaine.com"
              className="p-2 border rounded-md w-[250px] dark:bg-gray-800 dark:text-white"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="role" className="text-sm font-medium mb-1">
              Rôle à attribuer :
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="p-2 border rounded-md w-[200px] dark:bg-gray-800 dark:text-white"
              required
            >
              <option value="" disabled>
                -- Sélectionnez un rôle --
              </option>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md mt-4 shadow-md"
        >
          Ajouter & Envoyer l'invitation
        </button>
      </form>

      {/* Tableau récap */}
      {accreditations.length > 0 && (
        <div className="overflow-x-auto mx-auto w-full md:w-3/4 mb-10">
          <table className="table-auto w-full border border-gray-300 dark:border-gray-600">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="p-2 border">Email</th>
                <th className="p-2 border">Rôle</th>
                <th className="p-2 border">Lien</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800">
              {accreditations.map((acc) => (
                <tr key={acc.id} className="text-center">
                  <td className="p-2 border">{acc.user_email}</td>
                  <td className="p-2 border">{acc.role}</td>
                  <td className="p-2 border text-xs text-blue-600 underline">
                    <a
                      href={`https://www.scoreboard-judo-club.fr/competition/${acc.competition_id}/access?t=${acc.access_token}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Lien
                    </a>
                  </td>
                  <td className="p-2 border">
                    <button
                      onClick={() => handleDelete(acc.id)}
                      className="text-red-500 hover:underline"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AccreditationsPage;
